import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billing, ApiError } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Text, Button, Badge, SimpleGrid, Title } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, EmptyState, DataTable, MetricCard } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, NumberInput, Switch, Select, PhoneInput } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError, notifyInfo } from '@repletoryreach/sdk-ui/notifications';
import { formatMinor, formatDateTime, formatRelative } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useCredits } from '../../../lib/credits';
import { useT } from '../../../i18n/I18nProvider';
import { phoneLabels } from '../../../lib/phone';
import '../billing.css';
import { usePaymentProviders } from '../../../data/usePaymentProviders';
import { HelpButton } from '../../../app/guides/HelpButton';

/** Major units the inputs work in; the API stores minor units. */
const toMajor = (minor: number | null | undefined) => (minor == null ? '' : Math.round(minor) / 100);
const toMinor = (major: number | '') => (major === '' ? null : Math.round(major * 100));

function errorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: { message?: string } } | null;
    return body?.error?.message ?? e.message ?? fallback;
  }
  return e instanceof Error ? e.message : fallback;
}

export default function BillingAlertsPage() {
  const t = useT();
  const credits = useCredits();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();

  // Back from the gateway after saving a card: the webhook lands a moment
  // later, so refetch and say so rather than show the old list.
  const returned = params.get('method') === 'returned';
  useEffect(() => {
    if (!returned) return;
    qc.invalidateQueries({ queryKey: ['alerts'] });
    qc.invalidateQueries({ queryKey: ['wallet'] });
    notifyInfo(t('billing.alerts.methods.returned'), t('billing.alerts.title'));
    setParams((prev) => { const q = new URLSearchParams(prev); q.delete('method'); return q; }, { replace: true });
  }, [returned]); // eslint-disable-line react-hooks/exhaustive-deps

  const alertsQ = useQuery({ queryKey: ['alerts'], queryFn: () => billing.getAlerts() });
  const historyQ = useQuery({ queryKey: ['alerts', 'history'], queryFn: () => billing.listAlertHistory({ limit: 50 }) });
  const a = alertsQ.data?.data;
  const currency = a?.currency ?? 'ZAR';
  const methods = a?.payment_methods ?? [];

  // Form state, seeded from the server once and on every successful save.
  const [low, setLow] = useState<number | ''>('');
  const [email, setEmail] = useState('');
  const [msisdn, setMsisdn] = useState('');
  const [inApp, setInApp] = useState(true);
  const [dailySpend, setDailySpend] = useState<number | ''>('');
  const [autoOn, setAutoOn] = useState(false);
  const [autoThreshold, setAutoThreshold] = useState<number | ''>('');
  const [autoAmount, setAutoAmount] = useState<number | ''>('');
  const [autoCap, setAutoCap] = useState<number | ''>(3);
  const [methodId, setMethodId] = useState('');
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!a || seeded) return;
    setLow(toMajor(a.settings.low_balance_minor ?? null));
    setEmail(a.settings.notify_email ?? '');
    setMsisdn(a.settings.notify_msisdn ?? '');
    setInApp(a.settings.notify_in_app);
    setDailySpend(toMajor(a.settings.daily_spend_alert_minor ?? null));
    setAutoOn(a.settings.auto_topup_enabled);
    setAutoThreshold(toMajor(a.settings.auto_topup_threshold_minor ?? null));
    setAutoAmount(toMajor(a.settings.auto_topup_amount_minor ?? null));
    setAutoCap(a.settings.auto_topup_daily_cap);
    setMethodId(a.settings.payment_method_id ?? '');
    setSeeded(true);
  }, [a, seeded]);

  const save = useMutation({
    mutationFn: (req: billing.UpdateWalletAlertsRequest) => billing.updateAlerts(req),
    onSuccess: (res) => {
      qc.setQueryData(['alerts'], res);
      qc.invalidateQueries({ queryKey: ['wallet'] });
      setSeeded(false);
      notifySuccess(t('billing.alerts.saved'), t('billing.alerts.title'));
    },
    onError: (e) => notifyError(errorMessage(e, t('billing.alerts.saveFailed')), t('billing.alerts.title'))
  });

  function saveAlerts() {
    save.mutate({
      low_balance_minor: low === '' ? 0 : toMinor(low)!,
      notify_email: email,
      notify_msisdn: msisdn,
      notify_in_app: inApp,
      daily_spend_alert_minor: dailySpend === '' ? 0 : toMinor(dailySpend)!
    });
  }
  function saveAutoTopup() {
    save.mutate({
      auto_topup_enabled: autoOn,
      auto_topup_threshold_minor: autoThreshold === '' ? 0 : toMinor(autoThreshold)!,
      auto_topup_amount_minor: autoAmount === '' ? 0 : toMinor(autoAmount)!,
      auto_topup_daily_cap: autoCap === '' ? 3 : autoCap,
      ...(methodId ? { payment_method_id: methodId } : {})
    });
  }

  const test = useMutation({
    mutationFn: () => billing.sendTestAlert(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['alerts', 'history'] });
      const emailRow = res.data.sent.find((s) => s.channel === 'email');
      notifySuccess(
        emailRow?.status === 'sent'
          ? t('billing.alerts.test.sentTo', { target: emailRow.target ?? '' })
          : t('billing.alerts.test.recorded', { detail: emailRow?.detail ?? '' }),
        t('billing.alerts.title')
      );
    },
    onError: (e) => notifyError(errorMessage(e, t('billing.alerts.test.failed')), t('billing.alerts.title'))
  });

  // Saving a card goes through one provider. When the platform has switched it
  // off the button goes too, with a line saying so, instead of leading to an error.
  const providersQ = usePaymentProviders();
  const canSaveCard = !providersQ.data || providersQ.data.some((p) => p.saves_cards && p.available);

  const addCard = useMutation({
    mutationFn: () => billing.savePaymentMethod({ provider: 'paystack', return_url: `${window.location.origin}/billing/alerts?method=returned` }),
    onSuccess: (res) => {
      const url = res.data.authorization_url ?? res.data.redirect_url;
      if (!url) { notifyError(t('billing.alerts.methods.noUrl'), t('billing.alerts.title')); return; }
      openConfirmModal({
        title: t('billing.alerts.methods.confirm.title'),
        children: <Text role="bodySmall">{t('billing.alerts.methods.confirm.body', { amount: formatMinor(res.data.amount_minor, res.data.currency) })}</Text>,
        labels: { confirm: t('billing.alerts.methods.confirm.go'), cancel: t('action.cancel') },
        onConfirm: () => { window.location.href = url; }
      });
    },
    onError: (e) => notifyError(errorMessage(e, t('billing.alerts.methods.startFailed')), t('billing.alerts.title'))
  });

  const setPrimary = useMutation({
    mutationFn: (id: string) => billing.setPrimaryPaymentMethod(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['alerts'] }); qc.invalidateQueries({ queryKey: ['wallet'] }); setSeeded(false); }
  });
  const remove = useMutation({
    mutationFn: (id: string) => billing.removePaymentMethod(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['alerts'] }); qc.invalidateQueries({ queryKey: ['wallet'] }); setSeeded(false);
      if (!res.meta.auto_topup_enabled && autoOn) notifyInfo(t('billing.alerts.methods.autoOff'), t('billing.alerts.title'));
    }
  });
  const chargeNow = useMutation({
    mutationFn: () => billing.runAutoTopup(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['alerts'] }); qc.invalidateQueries({ queryKey: ['wallet'] }); qc.invalidateQueries({ queryKey: ['ledger'] }); setSeeded(false);
      if (res.data.succeeded) notifySuccess(t('billing.alerts.auto.charged', { amount: formatMinor(res.data.amount_minor, currency), balance: credits(res.data.balance_minor) }), t('billing.alerts.title'));
      else notifyError(res.data.detail, t('billing.alerts.auto.notCharged'));
    },
    onError: (e) => notifyError(errorMessage(e, t('billing.alerts.auto.notCharged')), t('billing.alerts.title'))
  });

  const methodOptions = useMemo(() => methods.map((m) => ({ value: m.id, label: m.is_primary ? t('billing.alerts.methods.primaryLabel', { label: m.label }) : m.label })), [methods, t]);
  const history = historyQ.data?.data ?? [];

  return (
    <Stack gap="lg">
      <PageHeader title={t('billing.alerts.title')} description={t('billing.alerts.description')} withDivider={false} actions={<HelpButton guide="account" />} />

      {alertsQ.error && <Alert color="error" role="alert">{t('billing.alerts.loadError')}</Alert>}

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <MetricCard label={t('billing.walletBalance')} value={a ? credits(a.balance_minor, currency) : '–'} loading={alertsQ.isPending}
          hint={a ? t(`billing.alerts.state.${a.state}`) : undefined} />
        <MetricCard label={t('billing.alerts.lowLine')} value={a ? credits(a.effective_low_balance_minor, currency) : '–'} loading={alertsQ.isPending}
          hint={a && a.settings.low_balance_minor == null ? t('billing.alerts.lowLine.default') : t('billing.alerts.lowLine.custom')} />
        <MetricCard label={t('billing.alerts.auto.title')} value={a ? (a.settings.auto_topup_enabled ? t('common.yes') : t('common.no')) : '–'} loading={alertsQ.isPending}
          hint={a?.auto_topup.last ? t('billing.alerts.auto.lastHint', { when: formatRelative(a.auto_topup.last.created_at), outcome: a.auto_topup.last.succeeded ? t('billing.alerts.auto.succeeded') : t('billing.alerts.auto.failed') }) : t('billing.alerts.auto.never')} />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Card withBorder data-guide="alerts-low">
          <Stack gap="md">
            <Stack gap={2}>
              <Title order={4}>{t('billing.alerts.low.title')}</Title>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.alerts.low.body')}</Text>
            </Stack>
            <NumberInput
              label={t('billing.alerts.low.threshold', { currency })}
              value={low}
              onChange={setLow}
              min={0}
              step={10}
              description={t('billing.alerts.low.thresholdHint', { currency, amount: a ? credits(a.default_low_balance_minor, currency) : '' })}
            />
            <TextInput
              label={t('billing.alerts.low.email')}
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              placeholder={t('billing.alerts.low.emailPlaceholder')}
              description={t('billing.alerts.low.emailHint')}
            />
            <PhoneInput
              label={t('billing.alerts.low.msisdn')}
              value={msisdn}
              onChange={(e164) => setMsisdn(e164)}
              description={`${t('phone.hint')} ${t('billing.alerts.low.msisdnHint')}`}
              labels={phoneLabels(t)}
            />
            <NumberInput
              label={t('billing.alerts.daily.threshold', { currency })}
              value={dailySpend}
              onChange={setDailySpend}
              min={0}
              step={50}
              description={t('billing.alerts.daily.thresholdHint')}
            />
            <Switch checked={inApp} onChange={setInApp} label={t('billing.alerts.low.inApp')} description={t('billing.alerts.low.inAppHint')} />
            <Group gap="sm">
              <Button size="sm" loading={save.isPending} onClick={saveAlerts}>{t('action.save')}</Button>
              <Button size="sm" variant="default" loading={test.isPending} onClick={() => test.mutate()}>{t('billing.alerts.test.action')}</Button>
            </Group>
          </Stack>
        </Card>

        <Card withBorder data-guide="alerts-auto">
          <Stack gap="md">
            <Stack gap={2}>
              <Title order={4}>{t('billing.alerts.auto.title')}</Title>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.alerts.auto.body')}</Text>
            </Stack>
            <Switch checked={autoOn} onChange={setAutoOn} label={t('billing.alerts.auto.enable')}
              description={methods.length === 0 ? t('billing.alerts.auto.needsCard') : undefined} disabled={methods.length === 0 && !autoOn} />
            <NumberInput label={t('billing.alerts.auto.threshold', { currency })} value={autoThreshold} onChange={setAutoThreshold} min={0} step={10}
              description={t('billing.alerts.auto.thresholdHint')} />
            <NumberInput label={t('billing.alerts.auto.amount', { currency })} value={autoAmount} onChange={setAutoAmount} min={0} step={50}
              description={t('billing.alerts.auto.amountHint')} />
            <NumberInput label={t('billing.alerts.auto.cap')} value={autoCap} onChange={setAutoCap} min={1} max={20}
              description={t('billing.alerts.auto.capHint', { count: a?.auto_topup.today_count ?? 0 })} />
            <Select
              label={t('billing.alerts.auto.method')}
              value={methodId}
              onChange={(e) => setMethodId(e.currentTarget.value)}
              data={[{ value: '', label: t('billing.alerts.auto.methodPrimary') }, ...methodOptions]}
              description={t('billing.alerts.auto.methodHint')}
            />
            <Group gap="sm">
              <Button size="sm" loading={save.isPending} onClick={saveAutoTopup}>{t('action.save')}</Button>
              <Button size="sm" variant="default" loading={chargeNow.isPending} disabled={methods.length === 0 || autoAmount === '' || autoAmount <= 0}
                onClick={() => openConfirmModal({
                  title: t('billing.alerts.auto.chargeNow'),
                  children: <Text role="bodySmall">{t('billing.alerts.auto.chargeNowBody', { amount: autoAmount === '' ? '' : formatMinor(toMinor(autoAmount)!, currency) })}</Text>,
                  labels: { confirm: t('billing.alerts.auto.chargeNow'), cancel: t('action.cancel') },
                  onConfirm: () => chargeNow.mutate()
                })}>
                {t('billing.alerts.auto.chargeNow')}
              </Button>
            </Group>
          </Stack>
        </Card>
      </SimpleGrid>

      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={2}>
              <Title order={4}>{t('billing.alerts.methods.title')}</Title>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.alerts.methods.body')}</Text>
            </Stack>
            {canSaveCard && (
              <Button size="sm" loading={addCard.isPending} onClick={() => addCard.mutate()}>{t('billing.alerts.methods.add')}</Button>
            )}
          </Group>
          {!canSaveCard && (
            <Alert color="info" title={t('billing.alerts.methods.unavailable.title')}>
              {t('billing.alerts.methods.unavailable.body')}
            </Alert>
          )}
          {!alertsQ.isPending && methods.length === 0 && (
            <EmptyState title={t('billing.alerts.methods.empty')} description={t('billing.alerts.methods.emptyHint')} />
          )}
          {methods.map((m) => (
            <Group key={m.id} justify="space-between" wrap="wrap" className="rr-billing__method">
              <Stack gap={0} style={{ minWidth: 0 }}>
                <Group gap={8}>
                  <Text role="bodySmall" fw={600}>{m.label}</Text>
                  {m.is_primary && <Badge size="xs" variant="light">{t('billing.alerts.methods.primary')}</Badge>}
                  {!m.reusable && <Badge size="xs" variant="light" color="orange">{t('billing.alerts.methods.notReusable')}</Badge>}
                </Group>
                <Text role="caption" c="var(--rr-color-text-muted)">
                  {m.exp_month && m.exp_year ? t('billing.alerts.methods.expires', { month: String(m.exp_month).padStart(2, '0'), year: m.exp_year }) : m.bank ?? ''}
                  {' · '}{t('billing.alerts.methods.added', { when: formatDateTime(m.created_at) })}
                </Text>
              </Stack>
              <Group gap="xs">
                {!m.is_primary && <Button size="xs" variant="default" loading={setPrimary.isPending} onClick={() => setPrimary.mutate(m.id)}>{t('billing.alerts.methods.makePrimary')}</Button>}
                <Button size="xs" variant="subtle" color="red" loading={remove.isPending} onClick={() => openConfirmModal({
                  title: t('billing.alerts.methods.remove.title'),
                  children: <Text role="bodySmall">{t('billing.alerts.methods.remove.body', { label: m.label })}</Text>,
                  labels: { confirm: t('action.remove'), cancel: t('action.cancel') },
                  confirmProps: { color: 'red' },
                  onConfirm: () => remove.mutate(m.id)
                })}>{t('action.remove')}</Button>
              </Group>
            </Group>
          ))}
        </Stack>
      </Card>

      <Card withBorder noPadding>
        <Stack gap="xs" p="md">
          <Title order={4}>{t('billing.alerts.history.title')}</Title>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.alerts.history.body')}</Text>
        </Stack>
        <DataTable<Indexed<billing.WalletNotification>>
          records={history as Indexed<billing.WalletNotification>[]}
          loading={historyQ.isPending}
          idAccessor="id"
          emptyState={t('billing.alerts.history.empty')}
          columns={[
            { accessor: 'created_at', title: t('common.date'), render: (r) => formatDateTime(r.created_at) },
            { accessor: 'kind', title: t('billing.alerts.history.kind'), render: (r) => <Badge size="xs" variant="light">{t(`billing.alerts.kind.${r.kind}`, r.kind)}</Badge> },
            { accessor: 'channel', title: t('billing.alerts.history.channel'), render: (r) => t(`billing.alerts.channel.${r.channel}`, r.channel) },
            { accessor: 'target', title: t('billing.alerts.history.target'), render: (r) => r.target ?? <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text> },
            {
              accessor: 'status', title: t('common.status'),
              render: (r) => <Badge size="xs" variant="light" color={r.status === 'sent' ? 'green' : r.status === 'failed' ? 'red' : 'gray'}>{t(`billing.alerts.status.${r.status}`, r.status)}</Badge>
            },
            { accessor: 'balance_minor', title: t('billing.walletBalance'), textAlign: 'right', render: (r) => credits(r.balance_minor, r.currency) },
            { accessor: 'detail', title: t('common.description'), render: (r) => <Text role="caption" c="var(--rr-color-text-muted)">{r.detail ?? ''}</Text> }
          ]}
        />
      </Card>

      <Text role="caption" c="var(--rr-color-text-muted)">
        {t('billing.alerts.footnote')} <Link to="/billing/usage">{t('billing.usage.title')}</Link> · <Link to="/billing/topup">{t('billing.topUp')}</Link>
        <Button size="xs" variant="subtle" onClick={() => nav('/billing')} style={{ marginLeft: 8 }}>{t('action.back')}</Button>
      </Text>
    </Stack>
  );
}
